// components/ZoomEmbed.tsx
// Zoom 問診を iframe でラップするコンポーネント
// PC: /zoom-room をフルページで iframe 描画（同一オリジン・localStorage 共有）
// スマホ: Zoom アプリへのリンクボタンを表示
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface ZoomEmbedProps {
  meetingNumber: string;
  displayName: string;
  joinUrl: string;
  needsTranslation?: boolean;
}

// スマホ判定（iPhone / Android / iPad）
const isMobileDevice = (): boolean =>
  /iPhone|Android|iPad/i.test(navigator.userAgent);

const ZoomEmbed: React.FC<ZoomEmbedProps> = ({ meetingNumber, displayName, joinUrl, needsTranslation = false }) => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type !== 'zoom-meeting-ended') return;
      const role = localStorage.getItem('role');
      if (role === 'doctor') {
        window.location.href = '/doctor/dashboard';
      } else if (role === 'admin') {
        window.location.href = '/admin/dashboard';
      } else {
        window.location.href = '/mypage';
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
  const appLang = i18n.language || 'ja';
  const zoomLang = appLang.startsWith('zh') ? 'zh-CN'
                 : appLang.startsWith('ja') ? 'ja-JP'
                 : 'en-US';

  // ── スマホ表示 ──────────────────────────────────────────────────
  if (isMobileDevice()) {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-4">
        <p className="text-sm text-gray-500 text-center">
          {t('consultation.mobile_hint_ja') || 'スマートフォンでは Zoom アプリから参加してください。'}
          <br />
          {t('consultation.mobile_hint_zh') || '请在智能手机上通过 Zoom 应用加入。'}
        </p>
        <a
          href={joinUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold text-sm hover:bg-blue-700 transition"
        >
          {t('consultation.join_mobile')}
        </a>
      </div>
    );
  }

  // ── PC: /zoom-room を iframe で埋め込む ──────────────────────────
  // 同一オリジンなので localStorage の JWT トークンを iframe 内で共有できる
  // /zoom-room.html は public/ 配下の静的ファイル（webpack を経由しない）
  const iframeSrc = `/zoom-room.html?meetingNumber=${encodeURIComponent(meetingNumber)}&displayName=${encodeURIComponent(displayName)}&joinUrl=${encodeURIComponent(joinUrl)}&lang=${zoomLang}${needsTranslation ? '&needsTranslation=1' : ''}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
      <iframe
        src={iframeSrc}
        title="Zoom Meeting"
        allow="camera; microphone; display-capture; autoplay; fullscreen"
        style={{ width: '100%', border: 'none', display: 'block', flex: 1, minHeight: '400px' }}
      />
      <p style={{
        margin: 0, padding: '4px 10px',
        fontSize: '11px', color: '#9ca3af', background: '#111827',
        textAlign: 'center', lineHeight: 1.5,
      }}>
        {t('consultation.zoom_ui_hint')}
      </p>
    </div>
  );
};

export default ZoomEmbed;
